import { visitorTrafficType } from './visitor-journey';
import { privateTelemetry } from './telemetry-privacy';
import type { CaptureResult } from 'posthog-js';

function collectStrings(value: unknown, out: string[]): void {
  if (value === null || value === undefined) return;
  if (typeof value === 'string') {
    out.push(value);
    return;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    out.push(String(value));
    return;
  }
  if (Array.isArray(value)) {
    for (const item of value) collectStrings(item, out);
    return;
  }
  if (typeof value === 'object') {
    for (const v of Object.values(value as Record<string, unknown>)) {
      collectStrings(v, out);
    }
  }
}

/**
 * Filters noisy or non-actionable exceptions so PostHog error tracking reflects
 * real app issues (see before_send in PostHog docs).
 */
export function posthogBeforeSend(cr: CaptureResult | null): CaptureResult | null {
  if (!cr) return cr;
  const trafficType = visitorTrafficType();
  if (trafficType) cr.properties.traffic_type = trafficType;
  // The SDK adds its public project key before calling this hook. Keep that
  // ingestion metadata intact. Only preserve the configured
  // routing key in this exact transport field; nested/private tokens stay redacted.
  const projectKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  const hasProjectToken = !!projectKey && cr.properties.token === projectKey;
  cr = privateTelemetry(cr);
  if (hasProjectToken) cr.properties.token = projectKey;
  if (cr.event !== '$exception') return cr;

  const props = cr.properties as Record<string, unknown>;
  const list = Array.isArray(props['$exception_list']) ? props['$exception_list'] : [];
  const head = list[0] as Record<string, unknown> | undefined;
  const exType = typeof head?.['$exception_type'] === 'string' ? head['$exception_type'] : '';
  const exMessage = typeof head?.['$exception_message'] === 'string' ? head['$exception_message'] : '';

  const strings: string[] = [];
  collectStrings(cr.properties, strings);
  const blob = strings.join('\n');

  // Fetch / navigation aborts (benign at this volume)
  if (exType === 'AbortError') return null;
  if (exType === 'DOMException' && /abort|aborted/i.test(exMessage)) return null;
  if (/signal is aborted without reason/i.test(blob)) return null;

  // Browser extension RPC noise (e.g. "Object Not Found Matching Id:3, MethodName:update")
  if (/object not found matching id:\d/i.test(blob) && /methodname:\s*update/i.test(blob)) {
    return null;
  }

  // Malformed external URLs (e.g. website field "http:")
  if (/unable to open a window with invalid url/i.test(blob) && /['"]http:['"]/i.test(blob)) {
    return null;
  }

  // Low-signal SecurityError from popups / embeds
  if (exType === 'SecurityError' && /the request was denied/i.test(exMessage)) return null;

  return cr;
}
